// DateRangeSelector.jsx
// react
import { useEffect, useState } from 'react';

// internationalization
// * i18n
import { useTranslation } from 'react-i18next';

// material ui icons
// * Icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

// material ui
// * MUI
import { Box, Button, Chip, Popover, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// feature-hooks
import { useEsgUrlDateRange } from '@/features/energyxplorer/hooks/useEsgUrlDateRange';

// relative
import {
  DatePicker,
  formatDate,
  normalizeRangeToZone,
  ZONE,
} from './datePickerUtils';
// * Shared DatePicker components
import { DatePickerStyles } from './DateRangePicker';

// * Luxon
import { DateTime } from 'luxon';

const shortcutsItems = [
  {
    label: '2026',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      return [now.startOf('year'), now.minus({ days: 1 }).endOf('day')];
    },
  },
  {
    label: '2025',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      const lastYearStart = now.minus({ years: 1 }).startOf('year');
      const lastYearEnd = now.minus({ years: 1 }).endOf('year');
      return [lastYearStart, lastYearEnd];
    },
  },
  {
    label: 'Sidste 7 dage',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      return [
        now.minus({ days: 7 }).startOf('day'),
        now.minus({ days: 1 }).endOf('day'),
      ];
    },
  },
  {
    label: 'Sidste 4 uger',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      return [
        now.minus({ weeks: 4 }).startOf('day'),
        now.minus({ days: 1 }).endOf('day'),
      ];
    },
  },
];

const EsgDateRangeSelector = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg')); // hides text below "sm" breakpoint

  // Centralized URL + Luxon handling
  const { range, setRange } = useEsgUrlDateRange({
    zone: 'Europe/Copenhagen',
    defaultDays: 30,
  });
  const [from, to] = range;

  const [anchorEl, setAnchorEl] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(() =>
    range[0] ? range[0].toJSDate() : null,
  );
  const [tempEndDate, setTempEndDate] = useState(() =>
    range[1] ? range[1].toJSDate() : null,
  );

  // Keep tempRange in sync if URL changes from somewhere else
  useEffect(() => {
    const [start, end] = normalizeRangeToZone(range);
    const setTempDate = async () => {
      setTempStartDate(start ? start.toJSDate() : null);
      setTempEndDate(end ? end.toJSDate() : null);
    };

    setTempDate();
  }, [range]);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleShortcutClick = (shortcut) => {
    const [start, end] = shortcut.getValue();
    setTempStartDate(start.toJSDate());
    setTempEndDate(end.toJSDate());
  };

  const handleConfirm = () => {
    if (!tempStartDate || !tempEndDate) return;

    const start = DateTime.fromJSDate(tempStartDate).setZone(ZONE);
    const end = DateTime.fromJSDate(tempEndDate).setZone(ZONE);

    setRange(start, end, {
      roundToDay: true,
      clampToNow: true,
    });

    setAnchorEl(null);
  };

  const handleClick = (event) => {
    const [start, end] = range;
    setTempStartDate(start ? start.toJSDate() : null);
    setTempEndDate(end ? end.toJSDate() : null);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <DatePickerStyles theme={theme} />

      <Tooltip
        title={t('daterange.select_period', { ns: 'energyxplorer' })}
        enterDelay={500}
        placement="top"
      >
        <Button
          startIcon={<CalendarTodayRoundedIcon />}
          variant="text"
          onClick={handleClick}
          disableRipple
          sx={{
            ':hover': { background: 'none' },
          }}
        >
          {!isSmall && from && to
            ? `${formatDate(from)} - ${formatDate(to)}`
            : !isSmall
              ? t('daterange.select_period', { ns: 'energyxplorer' })
              : null}
        </Button>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          className="custom-datepicker-wrapper"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Main Content - Shortcuts and Date Picker side by side */}
          <Box sx={{ display: 'flex' }}>
            {/* Shortcuts */}
            <Box
              sx={{
                p: 2,
                minWidth: 160,
              }}
            >
              <Stack direction="column" spacing={1}>
                {shortcutsItems.map((shortcut) => (
                  <Chip
                    key={shortcut.label}
                    label={shortcut.label}
                    onClick={() => handleShortcutClick(shortcut)}
                    size="medium"
                    sx={{
                      cursor: 'pointer',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Date Picker */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <DatePicker
                selected={tempStartDate}
                onChange={handleDateChange}
                startDate={tempStartDate}
                endDate={tempEndDate}
                selectsRange
                inline
                monthsShown={2}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                locale={'da'}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ p: 2, backgroundColor: 'background.paper' }}
          >
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{ ml: 1 }}
              disabled={!tempStartDate || !tempEndDate}
            >
              {t('buttonLabel.submit')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default EsgDateRangeSelector;
